import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  discardAsyncRoleplayMessage,
  finishAsyncRoleplaySession,
  getAsyncRoleplayErrorDetail,
  getAsyncRoleplaySession,
  retryAsyncRoleplayMessage,
  sendAsyncRoleplayVoice,
} from "../axios/asyncRoleplay";
import type { AsyncRoleplaySession } from "../types/asyncRoleplay";
import { clearAsyncRoleplaySession } from "../utils/asyncRoleplayStorage";

const POLL_INTERVAL_MS = 1500;
const MAX_RECORDING_SECONDS = 120;
const TIME_LIMIT_ERROR_SNIPPET = "time limit reached";
const EXCEEDS_REMAINING_ERROR_SNIPPET = "exceeds the remaining conversation time";

const needsPolling = (session: AsyncRoleplaySession | null): boolean => {
  if (!session) return false;
  if (session.status === "finishing") return true;
  if (session.status !== "active") return false;
  return session.messages.some(
    (message) =>
      message.status === "pending" ||
      message.status === "processing" ||
      message.transcription_status === "processing",
  );
};

export const isAsyncTimeLimitError = (error: unknown): boolean => {
  const detail = getAsyncRoleplayErrorDetail(error).toLowerCase();
  return detail.includes(TIME_LIMIT_ERROR_SNIPPET);
};

export const isAsyncExceedsRemainingTimeError = (error: unknown): boolean => {
  const detail = getAsyncRoleplayErrorDetail(error).toLowerCase();
  return detail.includes(EXCEEDS_REMAINING_ERROR_SNIPPET);
};

export const formatAsyncExceedsRemainingTimeMessage = (
  remainingSeconds: number,
): string => {
  const seconds = Math.max(0, Math.round(remainingSeconds));
  return `Your recording exceeds the remaining conversation time (${seconds} seconds). Please record a shorter message or finish the conversation.`;
};

export const isAsyncTimeBudgetExhausted = (
  session: AsyncRoleplaySession | null,
): boolean => {
  if (!session) return false;
  if (session.seconds_allowed == null || session.audio_seconds == null) {
    return false;
  }
  return session.audio_seconds >= session.seconds_allowed;
};

export const useAsyncRoleplaySession = (sessionId: string | null) => {
  const [session, setSession] = useState<AsyncRoleplaySession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const pendingUploadRef = useRef<{ messageId: string; blob: Blob } | null>(
    null,
  );
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!sessionId) return null;
      const next = await getAsyncRoleplaySession(sessionId, { signal });
      if (signal?.aborted) return null;
      setSession(next);
      setIsLoading(false);
      return next;
    },
    [sessionId],
  );

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setActionError(null);

    getAsyncRoleplaySession(sessionId, { signal: controller.signal })
      .then((next) => {
        if (!controller.signal.aborted) {
          setSession(next);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("Failed to load async roleplay session:", error);
        setActionError(getAsyncRoleplayErrorDetail(error));
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [sessionId]);

  const shouldPoll = needsPolling(session);

  // Chain polls after each request finishes so GETs never overlap.
  useEffect(() => {
    if (!sessionId || !shouldPoll) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pollController: AbortController | null = null;

    const scheduleNext = () => {
      timer = setTimeout(() => {
        void (async () => {
          pollController = new AbortController();
          try {
            const next = await refresh(pollController.signal);
            if (!cancelled && needsPolling(next)) {
              scheduleNext();
            }
          } catch (error) {
            if (pollController.signal.aborted || cancelled) return;
            console.error("Async roleplay poll failed:", error);
            // Network errors are not terminal; keep polling with the last snapshot.
            scheduleNext();
          } finally {
            pollController = null;
          }
        })();
      }, POLL_INTERVAL_MS);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      pollController?.abort();
    };
  }, [sessionId, shouldPoll, refresh]);

  const lastMessage = useMemo(() => {
    if (!session?.messages?.length) return null;
    return session.messages[session.messages.length - 1];
  }, [session]);

  const timeBudgetExhausted = useMemo(
    () => isAsyncTimeBudgetExhausted(session),
    [session],
  );

  const canRecord = useMemo(() => {
    if (!session || session.status !== "active" || isUploading) return false;
    if (timeBudgetExhausted) return false;
    if (!lastMessage) return true;
    if (lastMessage.status === "pending" || lastMessage.status === "processing") {
      return false;
    }
    if (lastMessage.status === "failed") return false;
    return lastMessage.status === "completed";
  }, [session, lastMessage, isUploading, timeBudgetExhausted]);

  const canFinish = useMemo(() => {
    if (!session || session.status !== "active" || isUploading) return false;
    if (lastMessage?.status === "pending" || lastMessage?.status === "processing") {
      return false;
    }
    if (lastMessage?.status === "failed") return false;
    const hasUserSpeech = session.messages.some(
      (message) =>
        message.message_id !== "opening" && Boolean(message.user_audio_url),
    );
    const audioSeconds = session.audio_seconds ?? 0;
    return hasUserSpeech && audioSeconds >= 10;
  }, [session, lastMessage, isUploading]);

  const uploadVoice = useCallback(
    async (blob: Blob, reuseMessageId?: string) => {
      if (!sessionId) return;
      const messageId = reuseMessageId || crypto.randomUUID();
      pendingUploadRef.current = { messageId, blob };
      setIsUploading(true);
      setActionError(null);

      try {
        const next = await sendAsyncRoleplayVoice(sessionId, messageId, blob);
        setSession(next);
        pendingUploadRef.current = null;
      } catch (error) {
        console.error("Failed to upload async voice message:", error);
        if (isAsyncTimeLimitError(error)) {
          pendingUploadRef.current = null;
          setActionError(null);
          try {
            const latest = await getAsyncRoleplaySession(sessionId);
            setSession(latest);
          } catch {
            // Keep going; screen still tries finish from the time-limit path.
          }
          return;
        }
        if (isAsyncExceedsRemainingTimeError(error)) {
          pendingUploadRef.current = null;
          const remaining = Math.max(
            0,
            (session?.seconds_allowed ?? 0) - (session?.audio_seconds ?? 0),
          );
          setActionError(formatAsyncExceedsRemainingTimeMessage(remaining));
          return;
        }
        setActionError(getAsyncRoleplayErrorDetail(error));
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [sessionId, session?.seconds_allowed, session?.audio_seconds],
  );

  const retryUpload = useCallback(async () => {
    const pending = pendingUploadRef.current;
    if (!pending) return;
    await uploadVoice(pending.blob, pending.messageId);
  }, [uploadVoice]);

  const retryMessage = useCallback(
    async (messageId: string) => {
      if (!sessionId) return;
      setActionError(null);
      try {
        const next = await retryAsyncRoleplayMessage(sessionId, messageId);
        setSession(next);
      } catch (error) {
        console.error("Failed to retry async message:", error);
        // 409 / busy: refresh snapshot so UI matches server state.
        try {
          const latest = await getAsyncRoleplaySession(sessionId);
          setSession(latest);
        } catch {
          // Keep prior snapshot; surface the retry error below.
        }
        setActionError(getAsyncRoleplayErrorDetail(error));
      }
    },
    [sessionId],
  );

  const retryFailedMessage = useCallback(async () => {
    if (!lastMessage || lastMessage.status !== "failed") return;
    await retryMessage(lastMessage.message_id);
  }, [lastMessage, retryMessage]);

  const retryTranscription = useCallback(
    async (messageId: string) => {
      await retryMessage(messageId);
    },
    [retryMessage],
  );

  const discardFailedMessage = useCallback(async () => {
    if (!sessionId || !lastMessage || lastMessage.status !== "failed") return;
    if (lastMessage.message_id === "opening") return;
    setActionError(null);
    try {
      const next = await discardAsyncRoleplayMessage(
        sessionId,
        lastMessage.message_id,
      );
      setSession(next);
    } catch (error) {
      console.error("Failed to discard async message:", error);
      setActionError(getAsyncRoleplayErrorDetail(error));
    }
  }, [sessionId, lastMessage]);

  const finish = useCallback(async () => {
    if (!sessionId) return;
    setActionError(null);
    try {
      const next = await finishAsyncRoleplaySession(sessionId);
      setSession(next);
    } catch (error) {
      console.error("Failed to finish async roleplay:", error);
      setActionError(getAsyncRoleplayErrorDetail(error));
      throw error;
    }
  }, [sessionId]);

  // When status becomes finished (finish call or poll), clear local resume
  // and ask Recordings/history to refresh.
  const finishedNotifiedRef = useRef<string | null>(null);
  useEffect(() => {
    if (session?.status !== "finished" || !session.session_id) return;
    if (finishedNotifiedRef.current === session.session_id) return;
    finishedNotifiedRef.current = session.session_id;
    clearAsyncRoleplaySession();
    window.dispatchEvent(
      new CustomEvent("asyncRoleplayFinished", {
        detail: {
          recordId: session.record_id,
          sessionId: session.session_id,
        },
      }),
    );
  }, [session?.status, session?.record_id, session?.session_id]);

  return {
    session,
    isLoading,
    isUploading,
    actionError,
    setActionError,
    canRecord,
    canFinish,
    timeBudgetExhausted,
    lastMessage,
    maxRecordingSeconds: MAX_RECORDING_SECONDS,
    refresh,
    uploadVoice,
    retryUpload,
    hasPendingUpload: !!pendingUploadRef.current,
    retryFailedMessage,
    retryTranscription,
    discardFailedMessage,
    finish,
  };
};
